import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MultiStepForm from '../components/MultiStepForm.jsx'
import WizardAside from '../components/WizardAside.jsx'
import Select from '../components/Select.jsx'
import { CheckIcon, CloseIcon, DownloadIcon, SparklesIcon, TrashIcon } from '../components/icons.jsx'
import { useAuth } from '../auth.jsx'
import { useManifest } from '../lib/data.jsx'
import { CATEGORIES, DEFAULT_CATEGORY, categoryPath, getCategory } from '../lib/categories.js'
import { getCachedCompanies } from '../lib/leadsCache.js'
import { useRbac } from '../lib/rbac.js'
import {
  IMPORT_FIELDS,
  MAX_FILE_BYTES,
  buildImportRows,
  deleteImportedRegion,
  guessMapping,
  importLeads,
  indexExisting,
  parseFile,
  parseSpreadsheet,
  summarizeRows,
} from '../lib/leadImport.js'
import { logEvent } from '../lib/events.js'
import { showToast } from '../lib/toast.js'

// Batch import - the same split-screen wizard as New Lead, copied from
// reachwired's ImportLeadType (2026-10-20). Three steps: hand over the
// spreadsheet and check which column is which, see every problem the rows have
// (and what will happen to each) before anything is sent, then import. Rows
// join the chosen directory as "<State> (imported)" regions, raw: nothing is
// enriched here - the directory's Enrich button researches them ten at a time.
const num = (v) => (Number(v) || 0).toLocaleString()
const rowsWord = (n) => (n === 1 ? 'row' : 'rows')

const INPUT_CLS =
  'w-full rounded-[10px] border border-line bg-panel px-[14px] py-[11px] text-[14px] font-normal text-ink placeholder:text-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none'

const SEVERITY = {
  error: { label: 'Not imported', cls: 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.16)] dark:text-[#fca5a5]' },
  warning: { label: 'Value dropped', cls: 'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]' },
  info: { label: 'Note', cls: 'bg-panel text-dim' },
}

function Stat({ label, value, tone = '' }) {
  return (
    <div className="rounded-[10px] border border-line bg-panel px-3.5 py-3">
      <div className={`text-[20px] font-bold leading-[1.15] tracking-[-0.02em] ${tone || 'text-ink'}`}>{value}</div>
      <div className="mt-1 text-[12px] leading-[1.35] text-dim">{label}</div>
    </div>
  )
}

const FIELD_OPTIONS = [{ value: '', label: 'Skip this column' }, ...IMPORT_FIELDS.map((f) => ({ value: f.key, label: f.label }))]

// Problems grouped for the check step: one line per kind, worst first, with
// how many rows it touched.
function groupIssues(counts, issues) {
  const severityOf = {}
  for (const i of issues) if (!severityOf[i.problem]) severityOf[i.problem] = i.severity
  const rank = { error: 0, warning: 1, info: 2 }
  return Object.entries(counts)
    .map(([problem, count]) => ({ problem, count, severity: severityOf[problem] || 'info' }))
    .sort((a, b) => rank[a.severity] - rank[b.severity] || b.count - a.count)
}

export default function ImportLeads() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { manifest, refresh } = useManifest()
  const { can } = useRbac()
  const [searchParams] = useSearchParams()
  const requested = searchParams.get('category')
  const initialCategory = getCategory(requested) ? requested : DEFAULT_CATEGORY

  // Import progress lives outside the form's data: it is not something the
  // person edits, and the final step re-renders from it while the send runs.
  const [progress, setProgress] = useState(null)
  const [removing, setRemoving] = useState('')
  const importing = useRef(false)
  const fileInput = useRef(null)

  // What the chosen directory already holds, for "skip what we have". Read
  // from the browser's own mirror - no request, and it is the same set of
  // rows the person sees in the table.
  const [existingByCategory, setExistingByCategory] = useState({})
  useEffect(() => {
    let alive = true
    for (const c of CATEGORIES) {
      getCachedCompanies(c.key)
        .then(({ rows }) => {
          if (alive) setExistingByCategory((m) => ({ ...m, [c.key]: indexExisting(rows) }))
        })
        .catch(() => {})
    }
    return () => {
      alive = false
    }
  }, [])

  const initialData = {
    category: initialCategory,
    parsed: null,
    mapping: {},
    fileName: '',
    pasted: '',
    skipExisting: true,
  }

  const build = (d) =>
    buildImportRows(d.parsed?.records || [], d.mapping || {}, {
      existing: existingByCategory[d.category] || null,
      skipExisting: d.skipExisting !== false,
    })

  function loadParsed({ headers, records, sheets, sheetsRead }, name, setField) {
    if (!headers.length || !records.length) {
      setField('parsed', { headers, records: [], error: 'No rows found. The first line must be the column headers.' })
      setField('mapping', {})
      return
    }
    setField('parsed', { headers, records, sheets: sheets || [], sheetsRead: sheetsRead || [], error: '' })
    setField('mapping', guessMapping(headers))
    setField('fileName', name)
  }

  async function onFile(file, setField) {
    if (!file) return
    if (file.size > MAX_FILE_BYTES) {
      setField('parsed', { headers: [], records: [], error: 'That file is over 25 MB. Split it and import the parts.' })
      return
    }
    try {
      loadParsed(await parseFile(file), file.name, setField)
    } catch (err) {
      setField('parsed', { headers: [], records: [], error: err?.message?.includes('xls') ? err.message : 'Could not read that file. CSV, TSV and .xlsx work.' })
    }
  }

  const importedRegions = (cat) => (manifest?.categories?.[cat] || []).filter((r) => r.imported)

  async function removeRegion(cat, region) {
    if (removing) return
    if (!window.confirm(`Remove all ${num(region.count)} leads in “${region.label}” for everyone? This cannot be undone.`)) return
    setRemoving(region.slug)
    try {
      const { removed } = await deleteImportedRegion(cat, region.slug)
      await refresh().catch(() => null)
      showToast({ tone: 'success', title: 'Imported leads removed', body: `${num(removed)} rows from ${region.label}.`, duration: 4000 })
    } catch (err) {
      showToast({ tone: 'error', title: 'Could not remove them', body: err?.message || 'Try again in a moment.', duration: 6000 })
    } finally {
      setRemoving('')
    }
  }

  const steps = [
    {
      id: 'file',
      title: 'Hand over the spreadsheet',
      description:
        'A CSV, tab-separated or .xlsx export with one company per row and the column names in the first line. Check the mapping - only mapped columns are imported.',
      canContinue: (d) => {
        if (!d.parsed?.records?.length) return 'Add a file or paste rows to continue.'
        if (!Object.values(d.mapping || {}).includes('name')) return 'Map one column to “Company name” - every lead needs one.'
        return true
      },
      render: (d, setField) => {
        const parsed = d.parsed
        const used = new Set(Object.values(d.mapping || {}).filter(Boolean))
        const sampleOf = (header) => {
          const hit = (parsed?.records || []).find((r) => String(r[header] ?? '').trim())
          return hit ? String(hit[header]).trim() : ''
        }
        const imported = importedRegions(d.category)
        return (
          <div className="mt-[26px] flex flex-col gap-y-5">
            {CATEGORIES.length > 1 ? (
              <label className="flex flex-col gap-y-1.5 text-[13px] font-medium text-ink">
                Import into
                <Select
                  value={d.category}
                  onChange={(v) => setField('category', v)}
                  ariaLabel="Directory to import into"
                  buttonClassName="w-full !rounded-[10px] !bg-panel !px-[14px] !py-[12px]"
                  options={CATEGORIES.map((c) => ({ value: c.key, label: c.plural }))}
                />
              </label>
            ) : (
              <div className="text-[13px] text-dim">
                Into <span className="font-semibold text-ink">{getCategory(d.category)?.plural}</span>, filed by state as
                new “(imported)” regions.
              </div>
            )}

            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,text/csv,text/tab-separated-values,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                onFile(file, setField)
              }}
            />
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-dashed border-line bg-panel px-4 py-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                onFile(e.dataTransfer?.files?.[0], setField)
              }}
            >
              <div className="flex min-w-0 items-center gap-x-3">
                <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px] bg-surface text-primary-dark [&>svg]:h-[18px] [&>svg]:w-[18px]">
                  <DownloadIcon />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-semibold text-ink">
                    {d.fileName || 'Drop a CSV or .xlsx here, or choose a file'}
                  </div>
                  <div className="text-[12.5px] text-dim">
                    {parsed?.records?.length
                      ? `${num(parsed.records.length)} rows · ${num(parsed.headers.length)} columns${
                          parsed.sheets?.length > 1
                            ? ` · ${parsed.sheetsRead.length} of ${parsed.sheets.length} sheets${
                                parsed.sheetsRead.length < parsed.sheets.length ? ' (the rest have other columns)' : ''
                              }`
                            : ''
                        }`
                      : 'Up to 25 MB. Blue Book, HubSpot, Apollo or a plain sheet all work.'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="inline-flex items-center rounded-[9px] border border-line bg-surface px-[14px] py-[8px] text-[13px] font-semibold text-ink transition-colors hover:bg-panel"
                onClick={() => fileInput.current?.click()}
              >
                {parsed?.records?.length ? 'Choose another file' : 'Choose file'}
              </button>
            </div>

            {!parsed?.records?.length && (
              <details className="rounded-[12px] border border-line bg-surface px-4 py-3">
                <summary className="cursor-pointer text-[13px] font-medium text-ink">Or paste rows instead</summary>
                <textarea
                  className={`${INPUT_CLS} mt-3 min-h-[120px] resize-y font-mono text-[12.5px] leading-[1.5]`}
                  placeholder={'name,website,city,state\nSooner Masonry,soonermasonry.com,Oklahoma City,OK'}
                  value={d.pasted || ''}
                  onChange={(e) => setField('pasted', e.target.value)}
                />
                <button
                  type="button"
                  disabled={!(d.pasted || '').trim()}
                  className="mt-3 inline-flex items-center rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => loadParsed(parseSpreadsheet(d.pasted || ''), 'Pasted rows', setField)}
                >
                  Use pasted rows
                </button>
              </details>
            )}

            {parsed?.error && (
              <div className="rounded-[10px] bg-[#fef2f2] px-3.5 py-2.5 text-[13px] font-medium text-[#b91c1c] dark:bg-[rgba(220,38,38,0.16)] dark:text-[#fca5a5]">
                {parsed.error}
              </div>
            )}

            {parsed?.records?.length > 0 && (
              <div className="overflow-hidden rounded-[12px] border border-line bg-surface">
                <div className="flex items-baseline justify-between gap-x-3 px-4 py-3 [border-bottom:1px_solid_var(--border)]">
                  <span className="text-[13px] font-semibold text-ink">Match the columns</span>
                  <span className="text-[12px] text-dim">
                    {used.has('name') ? (
                      <span className="inline-flex items-center gap-x-1 text-good">
                        <CheckIcon className="h-[13px] w-[13px]" /> Company name mapped · {num(used.size)} of{' '}
                        {num(parsed.headers.length)} columns used
                      </span>
                    ) : (
                      'Map one column to “Company name”'
                    )}
                  </span>
                </div>
                <div className="max-h-[340px] overflow-y-auto">
                  {parsed.headers.map((header) => {
                    const current = d.mapping?.[header] || ''
                    return (
                      <div
                        key={header}
                        className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_200px] items-center gap-x-3 px-4 py-2 [border-bottom:1px_solid_var(--border)] last:[border-bottom:none] max-[640px]:grid-cols-1 max-[640px]:gap-y-1.5"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-medium text-ink" title={header}>
                            {header}
                          </div>
                        </div>
                        <div className="truncate font-mono text-[12px] text-faint" title={sampleOf(header)}>
                          {sampleOf(header) || <span className="italic">empty</span>}
                        </div>
                        <Select
                          value={current}
                          ariaLabel={`Directory column for ${header}`}
                          onChange={(v) => setField('mapping', { ...(d.mapping || {}), [header]: v })}
                          buttonClassName={`w-full !py-[7px] !text-[13px] ${current ? '' : '!text-faint'}`}
                          options={FIELD_OPTIONS.map((o) => ({
                            ...o,
                            disabled: Boolean(o.value) && o.value !== current && used.has(o.value),
                          }))}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {imported.length > 0 && (
              <div className="rounded-[12px] border border-line bg-surface px-4 py-3">
                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.03em] text-faint">Imported so far</div>
                <ul className="m-0 flex list-none flex-col gap-y-1.5 pl-0">
                  {imported.map((r) => (
                    <li key={r.slug} className="flex items-center justify-between gap-x-3 text-[13px]">
                      <span className="min-w-0 truncate text-ink">
                        {r.label} <span className="tabular-nums text-faint">· {num(r.count)}</span>
                      </span>
                      {can('leads.removeImport') && (
                        <button
                          type="button"
                          disabled={Boolean(removing)}
                          onClick={() => removeRegion(d.category, r)}
                          className="inline-flex flex-none items-center gap-x-1 rounded-[8px] px-2 py-1 text-[12px] font-semibold text-dim transition-colors hover:bg-panel hover:text-danger disabled:opacity-50"
                          title="Remove these imported leads for everyone"
                        >
                          <TrashIcon className="h-[13px] w-[13px]" />
                          {removing === r.slug ? 'Removing…' : 'Remove'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'check',
      title: 'Check the rows',
      description:
        'Every row was validated against the column it came from. Nothing has been sent yet - fix the sheet and hand it over again, or import what passes.',
      canContinue: (d) => (build(d).rows.length ? true : 'No row passes - every one is missing a company name or already in the directory.'),
      render: (d, setField) => {
        const built = build(d)
        const groups = groupIssues(built.counts, built.issues)
        const total = d.parsed?.records?.length || 0
        const listed = built.issues.filter((i) => i.severity !== 'info' || i.problem !== 'No state').slice(0, 200)
        return (
          <div className="mt-[26px] flex flex-col gap-y-5">
            <div className="grid grid-cols-3 gap-2 max-[560px]:grid-cols-2">
              <Stat label={`of ${num(total)} ${rowsWord(total)} will import`} value={num(built.rows.length)} tone="text-good" />
              <Stat label="no company name - skipped" value={num(built.dropped)} tone={built.dropped ? 'text-danger' : ''} />
              <Stat label="duplicates in the file - merged" value={num(built.merged)} />
              <Stat label="already in the directory" value={num(built.existing)} />
              <Stat
                label="values dropped (bad email, phone, site, year)"
                value={num(built.issues.length ? groups.filter((g) => g.severity === 'warning' && !g.problem.startsWith('No website')).reduce((n, g) => n + g.count, 0) : 0)}
              />
              <Stat label="with no state - filed under Other" value={num(built.counts['No state'] || 0)} />
            </div>

            <label className="flex cursor-pointer items-start gap-x-2.5 text-[13px] text-ink">
              <input
                type="checkbox"
                className="mt-[3px]"
                checked={d.skipExisting !== false}
                onChange={(e) => setField('skipExisting', e.target.checked)}
              />
              <span>
                Skip companies already in {getCategory(d.category)?.plural || 'the directory'}
                <span className="block text-[12px] text-dim">
                  Same name plus the same phone, website or city as a lead you already have. Re-importing a sheet you
                  imported before updates those rows instead.
                </span>
              </span>
            </label>

            {groups.length > 0 ? (
              <div className="overflow-hidden rounded-[12px] border border-line bg-surface">
                <div className="px-4 py-3 text-[13px] font-semibold text-ink [border-bottom:1px_solid_var(--border)]">
                  What was found
                </div>
                <ul className="m-0 list-none pl-0">
                  {groups.map((g) => (
                    <li
                      key={g.problem}
                      className="flex items-center justify-between gap-x-3 px-4 py-2 text-[13px] [border-bottom:1px_solid_var(--border)] last:[border-bottom:none]"
                    >
                      <span className="flex min-w-0 items-center gap-x-2">
                        <span className={`flex-none rounded-full px-2 py-[2px] text-[11px] font-semibold ${SEVERITY[g.severity].cls}`}>
                          {SEVERITY[g.severity].label}
                        </span>
                        <span className="truncate text-ink">{g.problem}</span>
                      </span>
                      <span className="flex-none tabular-nums text-dim">
                        {num(g.count)} {rowsWord(g.count)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex items-center gap-x-2 rounded-[12px] border border-line bg-surface px-4 py-3 text-[13px] text-good">
                <CheckIcon className="h-[14px] w-[14px]" /> Every row passed - nothing to fix.
              </div>
            )}

            {listed.length > 0 && (
              <details className="rounded-[12px] border border-line bg-surface px-4 py-3" open={listed.length <= 12}>
                <summary className="cursor-pointer text-[13px] font-medium text-ink">
                  Row by row ({num(listed.length)}
                  {built.issues.length >= 500 ? '+' : ''})
                </summary>
                <div className="mt-3 max-h-[300px] overflow-y-auto">
                  <table className="w-full border-collapse text-left text-[12.5px]">
                    <thead className="sticky top-0 bg-surface text-[11.5px] uppercase tracking-[0.03em] text-faint">
                      <tr>
                        <th className="py-1.5 pr-3 font-semibold">Line</th>
                        <th className="py-1.5 pr-3 font-semibold">Column</th>
                        <th className="py-1.5 pr-3 font-semibold">Value</th>
                        <th className="py-1.5 font-semibold">What happens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listed.map((i, n) => (
                        <tr key={`${i.line}-${i.problem}-${n}`} className="align-top [border-top:1px_solid_var(--border)]">
                          <td className="py-1.5 pr-3 tabular-nums text-dim">{i.line}</td>
                          <td className="max-w-[120px] truncate py-1.5 pr-3 text-ink" title={i.column}>
                            {i.column || '—'}
                          </td>
                          <td className="max-w-[160px] truncate py-1.5 pr-3 font-mono text-faint" title={i.value}>
                            {i.value || '—'}
                          </td>
                          <td className="py-1.5 text-ink">
                            {i.problem} <span className="text-dim">· {i.action}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )
      },
    },
    {
      id: 'review',
      title: 'Review and import',
      description: 'What the directory will hold. The rows land raw - nothing is researched or charged until someone presses Enrich.',
      render: (d) => {
        const built = build(d)
        const s = summarizeRows(built.rows)
        const pct = (n) => (s.total ? `${Math.round((n / s.total) * 100)}%` : '0%')
        return (
          <div className="mt-[26px] flex flex-col gap-y-5">
            <div className="grid grid-cols-3 gap-2 max-[560px]:grid-cols-2">
              <Stat label="companies" value={num(s.total)} />
              <Stat label={`states · ${s.unplaced ? `${num(s.unplaced)} without one` : 'all placed'}`} value={num(s.states)} />
              <Stat label={`with a website (${pct(s.withWebsite)})`} value={num(s.withWebsite)} />
              <Stat label={`with an email (${pct(s.withEmail)})`} value={num(s.withEmail)} />
              <Stat label={`with a phone (${pct(s.withPhone)})`} value={num(s.withPhone)} />
              <Stat label={`with a named contact (${pct(s.withContact)})`} value={num(s.withContact)} />
            </div>

            {s.regions.length > 0 && (
              <div className="rounded-[12px] border border-line bg-surface px-4 py-3">
                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.03em] text-faint">
                  Filed into {getCategory(d.category)?.plural}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {s.regions.slice(0, 12).map((r) => (
                    <span
                      key={r.slug}
                      className="inline-flex items-center gap-x-1.5 rounded-full border border-line bg-panel px-2.5 py-[3px] text-[12px] font-medium text-ink"
                    >
                      {r.label}
                      <span className="tabular-nums text-faint">{num(r.count)}</span>
                    </span>
                  ))}
                  {s.regions.length > 12 && (
                    <span className="inline-flex items-center rounded-full px-2 py-[3px] text-[12px] text-faint">
                      +{s.regions.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}

            <p className="m-0 flex items-start gap-x-2 text-[12.5px] leading-[1.5] text-dim">
              <SparklesIcon className="mt-[2px] h-[14px] w-[14px] flex-none text-primary-dark" />
              After the import, press “Enrich next 10” in the directory to research ten of them on the spot - web
              research, Apollo contacts and a 0–10 score - then again for the next ten.
            </p>

            {progress && (
              <div className="rounded-[12px] border border-line bg-surface px-4 py-3">
                <div className="flex items-baseline justify-between text-[12.5px]">
                  <span className="font-semibold text-ink">
                    {progress.done ? 'Imported' : `Importing ${progress.region ? `· ${progress.region}` : ''}`}
                  </span>
                  <span className="tabular-nums text-dim">
                    {num(progress.sent)} / {num(progress.total)}
                  </span>
                </div>
                <div className="mt-2 h-[6px] overflow-hidden rounded-[3px] bg-line">
                  <div
                    className="h-full rounded-[3px] bg-primary transition-[width] duration-300"
                    style={{ width: `${progress.total ? Math.round((progress.sent / progress.total) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )
      },
    },
  ]

  async function handleComplete(d) {
    if (importing.current) return ''
    if (!can('leads.import')) return 'Your role can’t import leads. Ask an admin.'
    const built = build(d)
    if (!built.rows.length) return 'Nothing to import - no row passes.'
    importing.current = true
    setProgress({ sent: 0, total: built.rows.length, region: '', done: false })
    try {
      const result = await importLeads({
        category: d.category,
        rows: built.rows,
        onProgress: (p) => setProgress({ ...p, done: false }),
      })
      setProgress((p) => ({ ...(p || {}), sent: result.sent, total: result.sent, done: true }))
      await refresh().catch(() => null)
      const plural = getCategory(d.category)?.plural || d.category
      const refused = result.rejected.length
      showToast({
        tone: refused ? 'warn' : 'success',
        title: `${num(result.written)} leads imported`,
        body: `Into ${plural}, for everyone${refused ? ` · ${num(refused)} refused by the server (${result.rejected[0]?.reason}${result.rejected[0]?.line ? `, line ${result.rejected[0].line}` : ''})` : ''}. Press Enrich next 10 to research them.`,
        duration: 7000,
      })
      logEvent('leads_imported', {
        actor: user?.email || '',
        actorName: user?.name || '',
        detail: `Imported ${num(result.written)} leads into ${plural} (${result.regions.map((r) => r.label).join(', ')}) from ${d.fileName || 'a spreadsheet'}`,
      })
      const states = [...new Set(built.rows.map((r) => r._state).filter(Boolean))]
      navigate(`${categoryPath(d.category)}${states.length === 1 ? `?state=${states[0]}` : ''}`)
      return ''
    } catch (err) {
      setProgress(null)
      return err?.message || 'The import failed. Nothing more was written.'
    } finally {
      importing.current = false
    }
  }

  const previewSteps = useMemo(
    () => [
      ['Columns matched', 'Headers are matched to directory columns for you; change any you disagree with.'],
      ['Every row checked', 'Bad emails, phones, websites and years are named line by line - nothing fails silently.'],
      ['Enriched on the spot', 'Rows land raw. Enrich researches ten at a time - web research, Apollo contacts and a score.'],
    ],
    [],
  )

  return (
    <MultiStepForm
      steps={steps}
      initialData={initialData}
      finalLabel="Import leads"
      onComplete={handleComplete}
      onExit={() => navigate(categoryPath(initialCategory))}
      backLabel="Back to leads"
      aside={
        <WizardAside>
          <div className="w-[560px] max-w-full">
            <div className="rounded-[16px] border border-line bg-surface p-6 shadow-card">
              <div className="text-[12px] uppercase tracking-[0.04em] text-faint">Importing leads</div>
              <ol className="m-0 mt-3 flex flex-col gap-y-3 pl-0">
                {previewSteps.map(([t, s], i) => (
                  <li key={t} className="flex list-none gap-x-3">
                    <span className="grid h-[26px] w-[26px] flex-none place-items-center rounded-full bg-primary-tint text-[12px] font-bold text-primary-dark">
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold text-ink">{t}</div>
                      <div className="text-[12.5px] leading-[1.45] text-dim">{s}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-5 rounded-[12px] border border-line bg-panel p-4">
                <div className="text-[12px] font-semibold uppercase tracking-[0.03em] text-faint">Columns we read</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {IMPORT_FIELDS.slice(0, 14).map((f) => (
                    <span key={f.key} className="rounded-full border border-line bg-surface px-2.5 py-[3px] text-[12px] text-ink">
                      {f.label}
                    </span>
                  ))}
                </div>
                <p className="m-0 mt-3 flex items-start gap-x-2 text-[12.5px] leading-[1.45] text-dim">
                  <CloseIcon className="mt-[3px] h-[12px] w-[12px] flex-none" />
                  Columns you skip never leave the browser.
                </p>
              </div>
            </div>
          </div>
        </WizardAside>
      }
    />
  )
}
